import React, { useState, useEffect } from 'react';
import { AlertCircle, Phone, Filter, X, Search } from 'lucide-react';
import axios from 'axios';
import MentorLayout from '@/components/mentor/MentorLayout';

import { BACKEND_URL } from '../../config/api';
const StudentsNeedAttention = ({ user, onLogout }) => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    reason: '',
    school: '',
    grade: '',
    searchQuery: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Unique values for filters
  const [filterOptions, setFilterOptions] = useState({
    schools: [],
    grades: [],
    reasons: []
  });

  const severityColors = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    low: 'bg-blue-100 text-blue-800 border-blue-300'
  };

  const riskFlagLabels = {
    inactive_7days: '7+ Days Inactive',
    low_engagement: 'Low Engagement',
    poor_quiz_performance: 'Poor Quiz Scores',
    zero_progress: 'No Progress',
    fee_pending: 'Fees Pending'
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [students, filters]);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/mentor/attention/students`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudents(response.data);

      // Extract unique filter options
      const schools = [...new Set(response.data.map(s => s.school_name).filter(Boolean))];
      const grades = [...new Set(response.data.map(s => s.grade).filter(Boolean))];
      const reasons = [...new Set(response.data.flatMap(s => s.risk_flags))];

      setFilterOptions({ schools, grades, reasons });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching students:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...students];

    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(
        s =>
          s.name.toLowerCase().includes(query) ||
          s.email.toLowerCase().includes(query) ||
          s.student_id.toLowerCase().includes(query)
      );
    }

    // School filter
    if (filters.school) {
      filtered = filtered.filter(s => s.school_name === filters.school);
    }

    // Grade filter
    if (filters.grade) {
      filtered = filtered.filter(s => s.grade === filters.grade);
    }

    // Reason filter
    if (filters.reason) {
      filtered = filtered.filter(s => s.risk_flags.includes(filters.reason));
    }

    setFilteredStudents(filtered);
  };

  const handleCall = (student) => {
    // Navigate to follow-up page with student pre-filled
    window.location.href = `/follow-ups?student=${student.student_id}`;
  };

  const clearFilters = () => {
    setFilters({
      reason: '',
      school: '',
      grade: '',
      searchQuery: ''
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <MentorLayout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-600" />
              Students Need Attention
            </h1>
            <p className="text-gray-600 mt-2">
              {filteredStudents.length} students requiring follow-up calls
            </p>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Filter className="w-5 h-5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or student ID..."
            value={filters.searchQuery}
            onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-6 p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Advanced Filters</h3>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk Reason
              </label>
              <select
                value={filters.reason}
                onChange={(e) => setFilters({ ...filters, reason: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Reasons</option>
                {filterOptions.reasons.map(reason => (
                  <option key={reason} value={reason}>
                    {riskFlagLabels[reason] || reason}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                School
              </label>
              <select
                value={filters.school}
                onChange={(e) => setFilters({ ...filters, school: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Schools</option>
                {filterOptions.schools.map(school => (
                  <option key={school} value={school}>
                    {school}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grade
              </label>
              <select
                value={filters.grade}
                onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Grades</option>
                {filterOptions.grades.map(grade => (
                  <option key={grade} value={grade}>
                    Grade {grade}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Students Grid */}
      {filteredStudents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No students found
          </h3>
          <p className="text-gray-600">
            {activeFilterCount > 0
              ? 'Try adjusting your filters'
              : 'All students are doing well!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredStudents.map((student) => (
            <div
              key={student.student_id}
              className={`p-6 bg-white rounded-lg border-2 ${severityColors[student.severity]} shadow-sm hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Student Header */}
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {student.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {student.name}
                      </h3>
                      <p className="text-sm text-gray-600">{student.email}</p>
                    </div>
                    <span
                      className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold uppercase ${severityColors[student.severity]}`}
                    >
                      {student.severity}
                    </span>
                  </div>

                  {/* Student Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">School</p>
                      <p className="text-sm font-medium text-gray-900">
                        {student.school_name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Grade</p>
                      <p className="text-sm font-medium text-gray-900">
                        {student.grade || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">
                        {student.phone_number || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Last Login</p>
                      <p className="text-sm font-medium text-gray-900">
                        {student.last_login
                          ? new Date(student.last_login).toLocaleDateString()
                          : 'Never'}
                      </p>
                    </div>
                  </div>

                  {/* Risk Flags */}
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Risk Flags:</p>
                    <div className="flex flex-wrap gap-2">
                      {student.risk_flags.map((flag) => (
                        <span
                          key={flag}
                          className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium"
                        >
                          {riskFlagLabels[flag] || flag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Courses */}
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Enrolled Courses:</p>
                    <p className="text-sm text-gray-700">
                      {student.course_names.join(', ') || 'No courses'}
                    </p>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleCall(student)}
                  disabled={student.called_today}
                  className={`ml-4 px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                    student.called_today
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  <Phone className="w-5 h-5" />
                  {student.called_today ? 'Called Today' : 'Call Now'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </MentorLayout>
  );
};

export default StudentsNeedAttention;
